import type { HistoryEntry, PlanSession } from './types';

export const DEFAULT_REPORTING_TIME_ZONE = 'Asia/Tehran';

const dayFormatter = (timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });

const formatDay = (value: Date | string, formatter: Intl.DateTimeFormat): string | null => {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (!Number.isFinite(date.getTime())) return null;
  const parts = formatter.formatToParts(date);
  const part = (name: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === name)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};

export const getReportingDay = (value: Date | string, timeZone = DEFAULT_REPORTING_TIME_ZONE) =>
  formatDay(value, dayFormatter(timeZone));

export const enrichHistoryWithSessions = (
  history: HistoryEntry[],
  sessions: PlanSession[],
): HistoryEntry[] => {
  const byId = new Map(sessions.map((session) => [session.id, session]));
  return history.map((entry) => {
    const session = byId.get(entry.sessionId);
    if (!session || session.planId !== entry.planId) return entry;
    return { ...entry, sessionType: session.completed ? session.type : undefined };
  });
};

export interface DailyFocusSummary {
  focusMinutes: number;
  focusSessions: number;
  breakMinutes: number;
  breakSessions: number;
  unclassifiedMinutes: number;
  unclassifiedSessions: number;
}

/** History records completed sessions. Retained session evidence takes precedence over a history copy. */
export const summarizeDailyFocus = (
  history: HistoryEntry[],
  sessions: PlanSession[],
  day: string,
  timeZone = DEFAULT_REPORTING_TIME_ZONE,
): DailyFocusSummary => {
  const summary: DailyFocusSummary = {
    focusMinutes: 0,
    focusSessions: 0,
    breakMinutes: 0,
    breakSessions: 0,
    unclassifiedMinutes: 0,
    unclassifiedSessions: 0,
  };
  const formatter = dayFormatter(timeZone);
  const byId = new Map(sessions.map((session) => [session.id, session]));
  const seen = new Set<string>();
  const newestFirst = [...history].sort(
    (a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0),
  );

  for (const entry of newestFirst) {
    if (!Number.isFinite(entry.duration) || entry.duration <= 0) continue;
    const foundSession = byId.get(entry.sessionId);
    const session = foundSession?.planId === entry.planId ? foundSession : undefined;
    if (session && !session.completed) continue;
    const completedAt =
      session?.endedAt && Number.isFinite(Date.parse(session.endedAt))
        ? session.endedAt
        : entry.createdAt;
    const entryDay = formatDay(completedAt, formatter);
    if (!entryDay) continue;
    const evidenceId = entry.sessionId
      ? `${entry.planId}:${entry.sessionId}`
      : `history:${entry.id}`;
    if (seen.has(evidenceId)) continue;
    seen.add(evidenceId);
    if (entryDay !== day) continue;
    const type = session?.type ?? entry.sessionType;

    if (type === 'work') {
      summary.focusMinutes += entry.duration;
      summary.focusSessions += 1;
    } else if (type === 'short_break' || type === 'long_break') {
      summary.breakMinutes += entry.duration;
      summary.breakSessions += 1;
    } else {
      summary.unclassifiedMinutes += entry.duration;
      summary.unclassifiedSessions += 1;
    }
  }
  return summary;
};
